import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";

import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import useTheme from "@mui/material/styles/useTheme";

import { merge } from "lodash";

import { useAppDispatch } from "app/hooks";
import Dialog from "components/dialog";
import PatientSalesSelectList from "features/patients/sections/patient-billing/components/patient-sales-select-list";
import { useLazyGetMergeTagsSaleByIdQuery } from "services/merge-tags";
import { type Sale } from "services/types/sales.type";

import { selectDataParamsById, setDataParams } from "../word-processor.slice";

type SelectSaleDialogValues = {
  // Only `id` is read. The full Sale makes react-hook-form's FieldPath union huge and stalls
  // `yarn type-check` (ENG-2531). At runtime the field still holds the picked Sale.
  sale: Pick<Sale, "id"> | null;
};

interface WordProcessorSelectSaleDialogProps {
  id: string;
  // The host editor's nested-dialog layer, when it sits inside a raised modal (the scribe
  // modal). This dialog opens from the templates dialog on that layer, so it goes one above.
  nestedDialogZIndex?: number;
}

export default function WordProcessorSelectSaleDialog({
  id,
  nestedDialogZIndex
}: WordProcessorSelectSaleDialogProps) {
  const { zIndex } = useTheme();
  const dispatch = useAppDispatch();
  const dataParams =
    useSelector((state: any) => selectDataParamsById(state, id)) || {};

  const [isBusy, setIsBusy] = useState(false);

  const [prefetchSale] = useLazyGetMergeTagsSaleByIdQuery();

  const defaultValues: SelectSaleDialogValues = useMemo(() => {
    return merge({}, { sale: null });
  }, []);

  const {
    handleSubmit,
    control,
    reset,
    formState: { isValid }
  } = useForm<SelectSaleDialogValues>({
    mode: "onBlur",
    defaultValues
  });

  const handleClose = useCallback(() => {
    reset();
    setIsBusy(false);
    dispatch(setDataParams({ id, openSelectSaleDialog: false }));
  }, [dispatch, id, reset]);

  const handleSave = useCallback(
    async (data: SelectSaleDialogValues) => {
      setIsBusy(true);
      dispatch(setDataParams({ id, saleId: data.sale?.id }));
      await prefetchSale({ id: Number(data.sale?.id) });
      handleClose();
    },
    [dispatch, handleClose, id, prefetchSale]
  );

  return (
    <Dialog
      title="Please, select invoice"
      onClose={handleClose}
      open={Boolean(
        id && dataParams.openSelectSaleDialog && dataParams.patientId
      )}
      dialogContentProps={{
        sx: {
          display: "flex",
          alignItems: "center",
          pb: "20px",
          flexDirection: "column"
        }
      }}
      sx={{
        zIndex:
          nestedDialogZIndex === undefined
            ? zIndex.snackbar + 3
            : nestedDialogZIndex + 1
      }}
    >
      <Box height={320} maxHeight={320} width="100%" mb={1}>
        <PatientSalesSelectList
          control={control}
          patientId={dataParams.patientId}
          title=""
          showSelected
        />
      </Box>
      <DialogActions>
        <Button variant="outlined" size="small" onClick={handleClose}>
          Cancel
        </Button>

        <LoadingButton
          loading={isBusy}
          disabled={!isValid}
          variant="contained"
          size="small"
          onClick={handleSubmit(handleSave)}
        >
          Select
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
